import type { THandoverSetting } from '@conversed/shared/src/internal/models/app-config.type';
import dayjs from 'dayjs';

import { WEEK_DAYS_SHORT } from './Calendar.constants';
import type { Exclude, Interval, TDayJsDate } from './Calendar.types';

export function getDateRangLabel(date: TDayJsDate, rowsRendered: number) {
  const currentYear = dayjs().format('YYYY');

  const startDate = dayjs(date).startOf('week').add(1, 'days');
  const startYear = dayjs(startDate).format('YYYY');
  const startMonth = dayjs(startDate).format('MMM');
  const startDay = dayjs(startDate).format('D');

  const endDate = startDate.add(rowsRendered * 7 - 1, 'days');
  const endDay = dayjs(endDate).format('D');
  const endMonth = dayjs(endDate).format('MMM');
  const endYear = dayjs(endDate).format('YYYY');

  const isCurrentMonth = startMonth === endMonth;
  const isCurrentYear = currentYear === startYear;

  return `${startDay} ${!isCurrentMonth || !isCurrentYear ? startMonth : ''} ${
    !isCurrentYear ? startYear : ''
  } - ${endDay} ${endMonth} ${endYear}`;
}

export function getDayLabel(date: dayjs.Dayjs) {
  const isToday = date.diff(dayjs().startOf('day'), 'days') === 0;
  if (isToday) {
    return 'Today';
  }
  return date.format('D') === '1' ? date.format('MMM D') : date.format('D');
}

// Gathering one time and recurring availabilities for specific date
export function getCurrentAvailabilities(
  date: string,
  allAvailabilities: THandoverSetting[],
  filter: string | undefined,
) {
  const intervals: Interval[] = [];
  const dateIndex = findDayOrDate(allAvailabilities, date);

  const availabilityForDate = allAvailabilities[dateIndex];

  if (availabilityForDate?.isHoliday) {
    return [];
  }

  if (availabilityForDate) {
    const { slots } = availabilityForDate;
    slots.forEach((item) => {
      if (filter === 'all' || filter === item.type)
        intervals.push({
          ...item,
          recurring: false,
        });
    });
  }

  const formattedDay = dayjs(date).format('ddd').toLowerCase();
  const dayIndex = findDayOrDate(allAvailabilities, formattedDay);

  const availabilityForDay = allAvailabilities[dayIndex];

  if (availabilityForDay) {
    const { slots } = availabilityForDay;
    slots.forEach((item) => {
      if (filter === 'all' || filter === item.type) {
        const include = availabilityForDay.exclude
          ? availabilityForDay?.exclude?.findIndex(
              (element) => element.date === date && element.id === item.id,
            ) === -1
          : true;

        intervals.push({
          ...item,
          recurring: true,
          include,
        });
      }
    });
  }

  return intervals;
}

export function findDayOrDate(
  availabilities: THandoverSetting[],
  date: string,
) {
  const dateIndex: number = availabilities.findIndex((obj) => obj.day === date);
  return dateIndex;
}

// Apply recurring availabilities to multiple days
export function applyToMultipleDays(
  pastAvailabilities: THandoverSetting[],
  newAvailabilities: Interval[],
  days: string[],
) {
  const pA = JSON.parse(JSON.stringify(pastAvailabilities));
  days?.map((day: string) => {
    const objIndex: number = findDayOrDate(pA, day);
    if (objIndex !== -1) {
      pA[objIndex].slots = newAvailabilities;
    } else {
      pA.push({
        day,
        slots: newAvailabilities,
      });
    }
  });
  return pA;
}

// Apply recurring availabilities to specific day
export function applyToDay(
  pastAvailabilities: THandoverSetting[],
  newAvailabilities: Interval[],
  selectedDate: string,
) {
  const dateIndex: number = findDayOrDate(pastAvailabilities, selectedDate);
  const pA = JSON.parse(JSON.stringify(pastAvailabilities));
  if (dateIndex !== -1) {
    pA[dateIndex].slots = [...newAvailabilities];
  } else {
    pA.push({
      day: selectedDate,
      slots: [...newAvailabilities],
    });
  }
  return pA;
}

// Apply availabilities to specific date
export function applyDateToAvailability(
  pastAvailabilities: THandoverSetting[],
  newSlots: Interval[],
  isHoliday: boolean,
  selectedDate: string,
) {
  const pA: THandoverSetting[] = JSON.parse(JSON.stringify(pastAvailabilities));
  const dateIndex: number = findDayOrDate(pA, selectedDate);
  if (dateIndex === -1) {
    pA.push({
      day: selectedDate,
      slots: isHoliday ? [] : [...newSlots],
      isHoliday,
    });
  } else {
    pA[dateIndex].slots = isHoliday ? [] : [...newSlots];
    pA[dateIndex].isHoliday = isHoliday;
  }
  return pA;
}

export const filterOutOldAvailabilities = (
  availabilities: THandoverSetting[],
) => {
  // * Get start of week
  const startOfWeek = dayjs().startOf('week').add(1, 'days');
  const startOfWeekDay = startOfWeek.unix() / 86400;

  return availabilities.filter((availability) => {
    // * If availability is of type day then do not filter out
    if (WEEK_DAYS_SHORT.includes(availability.day)) {
      return true;
    }

    const availabilityDay = dayjs(availability.day).unix() / 86400;
    // * Difference between days should be less than or equal to 0 (today or in the future)
    return startOfWeekDay - availabilityDay <= 0;
  });
};

// Filter not recurring intervals from new availabilities
export function getNewSlots(newAvailabilities: Interval[]) {
  const newSlots: Interval[] = [];
  newAvailabilities.forEach((item: Interval) => {
    if (item.recurring === false) {
      newSlots.push({
        from: item.from,
        to: item.to,
        id: item.id,
        type: item.type,
        targetId: item.targetId,
      });
    }
  });
  return newSlots;
}

export function excludeDateFromDayIntervals(
  pastAvailabilities: THandoverSetting[],
  interval: Interval,
  dayIndex: number,
  selectedDate: string,
) {
  const pA = [...pastAvailabilities];
  const exclude: Exclude[] = [...(pA[dayIndex]?.exclude || [])];

  exclude.push({
    date: selectedDate,
    id: interval.id,
  });

  // We create a copy of the object before modifying it because JavaScript objects are passed by reference,
  // not by value. This means that if we modify the object directly, we would also be modifying the original
  // object in the pastAvailabilities array. By creating a copy, we ensure that we're only modifying the copy
  // and not the original object. This is important in cases where the original object or its properties are
  // read-only, as trying to modify a read-only property would result in a TypeError.
  const modifiedDayObject = { ...pA[dayIndex], exclude: exclude };
  pA[dayIndex] = modifiedDayObject;

  return pA;
}

// Removing date from recurring availabilitie's exclude list
export function includeDateToDayIntervals(
  pastAvailabilities: THandoverSetting[],
  interval: Interval,
  dayIndex: number,
  selectedDate: string,
) {
  const pA = [...pastAvailabilities];
  const exclude: Exclude[] = [...(pA[dayIndex]?.exclude || [])];
  const index = exclude.findIndex(
    (element) => element.date === selectedDate && element.id === interval.id,
  );

  if (index > -1) {
    exclude.splice(index, 1);
  }

  // We create a copy of the object before modifying it because JavaScript objects are passed by reference,
  // not by value. This means that if we modify the object directly, we would also be modifying the original
  // object in the pastAvailabilities array. By creating a copy, we ensure that we're only modifying the copy
  // and not the original object. This is important in cases where the original object or its properties are
  // read-only, as trying to modify a read-only property would result in a TypeError.
  const modifiedDayObject = { ...pA[dayIndex], exclude: exclude };
  pA[dayIndex] = modifiedDayObject;

  return pA;
}

// Adding date to recurring avalabilitie's exclude list
export function excludeIntervalsFromAvailability(
  pastAvailabilities: THandoverSetting[],
  newAvailabilities: Interval[],
  selectedDate: string,
) {
  const formattedDay = dayjs(selectedDate).format('ddd').toLowerCase();
  const dayIndex: number = findDayOrDate(pastAvailabilities, formattedDay);
  let pA = [...pastAvailabilities];
  newAvailabilities.forEach((item: Interval) => {
    if (item.recurring === true) {
      if (item.include === false) {
        pA = excludeDateFromDayIntervals(pA, item, dayIndex, selectedDate);
      } else {
        pA = includeDateToDayIntervals(pA, item, dayIndex, selectedDate);
      }
    }
  });
  return pA;
}
